// Pure helpers for the native OTP inputs. Kept free of React/DOM so they can be
// unit tested and reused by both manual typing and auto-capture (paste / iOS
// one-time-code autofill / Android WebOTP).

/**
 * Compute the next OTP box array from a raw input value.
 *
 * Handles three cases:
 *   - empty string  -> the box was cleared
 *   - single digit  -> normal typing, advance focus
 *   - many digits   -> paste / autofill / WebOTP, spread across boxes
 *
 * @param {string[]} prevOtp   current per-box characters
 * @param {number}   index     box that fired the change
 * @param {string}   rawValue  raw input value (may contain non-digits)
 * @param {number}   otpLength total number of boxes
 * @returns {{ otp: string[], focusIndex: number, addedDigit: boolean, filledAll: boolean }}
 */
export const fillOtpFromInput = (prevOtp, index, rawValue, otpLength) => {
  const digits = String(rawValue == null ? "" : rawValue).replace(/\D/g, "");
  const next = Array.from({ length: otpLength }, (_, i) => prevOtp[i] || "");

  if (digits.length === 0) {
    next[index] = "";
    return { otp: next, focusIndex: index, addedDigit: false, filledAll: false };
  }

  if (digits.length === 1) {
    next[index] = digits;
    return {
      otp: next,
      focusIndex: Math.min(index + 1, otpLength - 1),
      addedDigit: true,
      filledAll: next.every((d) => d !== ""),
    };
  }

  // Bulk value. A full-length code always starts at the first box (that is what
  // autofill / WebOTP deliver); a shorter paste starts at the active box.
  const startAt = digits.length >= otpLength ? 0 : index;
  for (let k = 0; k < digits.length && startAt + k < otpLength; k += 1) {
    next[startAt + k] = digits[k];
  }
  const lastFilled = Math.min(startAt + digits.length, otpLength) - 1;
  return {
    otp: next,
    focusIndex: Math.max(0, Math.min(lastFilled, otpLength - 1)),
    addedDigit: true,
    filledAll: next.every((d) => d !== ""),
  };
};

/** Whether the current browser exposes the WebOTP SMS retriever API. */
export const isWebOtpSupported = () =>
  typeof window !== "undefined" &&
  "OTPCredential" in window &&
  !!(navigator.credentials && navigator.credentials.get);

/**
 * Start the WebOTP SMS listener (Android Chrome). Resolves the received code via
 * `onCode`; all failures (unsupported, user dismissed, aborted) are swallowed.
 *
 * @param {(code: string) => void} onCode  called with the digits-only code
 * @param {number} otpLength               trim the code to this many digits
 * @returns {() => void} cleanup that aborts the pending listener
 */
export const startWebOtpListener = (onCode, otpLength) => {
  if (!isWebOtpSupported()) return () => {};
  const controller = new AbortController();
  navigator.credentials
    .get({ otp: { transport: ["sms"] }, signal: controller.signal })
    .then((credential) => {
      const code = String(credential && credential.code ? credential.code : "")
        .replace(/\D/g, "")
        .slice(0, otpLength);
      if (code) onCode(code);
    })
    .catch(() => {});
  return () => controller.abort();
};
